// Meeting Form with Virtual Room Integration
import React, { useState, useEffect } from 'react';
import ResponsiveModal from './ResponsiveModal';
import { Video, Plus, Users, MapPin, Calendar as CalendarIcon, Clock } from 'lucide-react';

interface MeetingFormProps {
  onAdd: (item: { title: string; dueDate?: string; notes?: string; metadata?: any }) => void;
  editingItem?: any; // Item to edit (if provided)
}

export default function MeetingForm({ onAdd, editingItem }: MeetingFormProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [listName, setListName] = useState('');
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [location, setLocation] = useState('');
  const [attendees, setAttendees] = useState('');
  const [agenda, setAgenda] = useState('');
  const [scheduleVirtualRoom, setScheduleVirtualRoom] = useState(false);
  const [meetingCategory, setMeetingCategory] = useState<'work' | 'home' | 'travel' | 'class'>('work');

  // Populate form when editing - watch for changes to editingItem
  useEffect(() => {
    if (editingItem) {
      console.log('[MeetingForm] Editing item:', editingItem);
      setIsOpen(true);
      setListName(editingItem.metadata?.listName || '');
      setTitle(editingItem.title || '');
      
      // Parse date and time from dueDate
      if (editingItem.dueDate) {
        const d = new Date(editingItem.dueDate);
        setDate(d.toISOString().split('T')[0]);
        setStartTime(editingItem.metadata?.startTime || d.toTimeString().slice(0, 5));
      }
      
      setEndTime(editingItem.metadata?.endTime || '');
      setLocation(editingItem.metadata?.location || '');
      setAttendees(editingItem.metadata?.attendees?.join(', ') || '');
      setAgenda(editingItem.metadata?.agenda || editingItem.notes || '');
      setScheduleVirtualRoom(!!editingItem.metadata?.virtualRoom);
    } else {
      // Reset form when not editing
      setListName('');
      setTitle('');
      setDate('');
      setStartTime('');
      setEndTime('');
      setLocation('');
      setAttendees('');
      setAgenda('');
      setScheduleVirtualRoom(false);
    }
  }, [editingItem]);

  const generateRoomId = () => {
    // Use category in room ID so it's recognizable
    return `${meetingCategory}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!listName.trim()) {
      alert('Please name your meeting group!');
      return;
    }
    if (!title.trim() || !date || !startTime) return;

    // Combine date and time for dueDate
    const meetingDateTime = `${date}T${startTime}:00.000Z`;
    
    // Generate virtual room link if checkbox is checked
    const roomId = scheduleVirtualRoom ? generateRoomId() : null;
    const roomUrl = roomId ? `${window.location.origin}/organizer?room=${roomId}&autojoin=1` : null;
    
    // Update location with virtual room link if scheduled
    const finalLocation = scheduleVirtualRoom 
      ? (location ? `${location}\n🎥 Virtual Room: ${roomUrl}` : `🎥 Virtual Room: ${roomUrl}`)
      : location;
    
    onAdd({
      title: title.trim(),
      dueDate: meetingDateTime,
      notes: agenda || undefined,
      metadata: {
        type: 'meeting',
        listName: listName.trim(),
        category: meetingCategory, // NEW: Store which category this meeting belongs to
        startTime,
        endTime: endTime || null,
        location: finalLocation || null,
        attendees: attendees ? attendees.split(',').map(a => a.trim()) : [],
        agenda: agenda || null,
        virtualRoom: scheduleVirtualRoom ? {
          roomId,
          roomUrl,
          scheduled: true,
          category: meetingCategory // NEW: Store category in virtual room data too
        } : null
      }
    });

    // Reset form
    setListName('');
    setTitle('');
    setDate('');
    setStartTime('');
    setEndTime('');
    setLocation('');
    setAttendees('');
    setAgenda('');
    setScheduleVirtualRoom(false);
    setMeetingCategory('work'); // Reset to default
    setIsOpen(false);
  };

  // Close handler that also resets editing state
  const handleClose = () => {
    setIsOpen(false);
    // Reset form fields
    setListName('');
    setTitle('');
    setDate('');
    setStartTime('');
    setEndTime('');
    setLocation('');
    setAttendees('');
    setAgenda('');
    setScheduleVirtualRoom(false);
  };

  return (
    <>
      {/* Add Button - only show if not editing */}
      {!editingItem && (
        <button
          onClick={() => setIsOpen(true)}
          className="w-full md:w-auto px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors touch-manipulation"
        >
          <Video className="w-5 h-5" />
          Add Meeting
        </button>
      )}

      {/* Form Modal - Full screen on mobile */}
      <ResponsiveModal
        isOpen={isOpen}
        onClose={handleClose}
        title={editingItem ? "Edit Meeting" : "Add Work Meeting"}
        maxWidth="full"
        fullScreen={true}
      >
        <form onSubmit={handleSubmit} className="space-y-4 h-full overflow-y-auto pb-20">
          {/* Meeting Group Name */}
          <div>
            <label className="block text-sm font-semibold text-slate-300 mb-2">
              Meeting Group Name *
            </label>
            <input
              type="text"
              value={listName}
              onChange={(e) => setListName(e.target.value)}
              placeholder="e.g., Team Meetings, Client Calls, Project Reviews"
              className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
              required
              autoFocus
            />
            <p className="text-xs text-slate-500 mt-1">Name this meeting group to organize related meetings</p>
          </div>

          {/* Meeting Title */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Meeting Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
              placeholder="e.g., Team Standup, Client Review"
              required
              autoFocus
            />
          </div>

          {/* Date and Time Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-1">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                <CalendarIcon className="w-4 h-4 inline mr-1" />
                Date *
              </label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                <Clock className="w-4 h-4 inline mr-1" />
                Start Time *
              </label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                <Clock className="w-4 h-4 inline mr-1" />
                End Time
              </label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
              />
            </div>
          </div>

          {/* Meeting Category Selector - NEW */}
          <div>
            <label className="block text-sm font-semibold text-slate-300 mb-2">
              Meeting Category *
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMeetingCategory('work')}
                className={`px-4 py-3 rounded-lg font-semibold text-sm transition-all ${
                  meetingCategory === 'work'
                    ? 'bg-indigo-600 text-white border-2 border-indigo-400'
                    : 'bg-slate-800 text-slate-400 border-2 border-slate-600 hover:border-slate-500'
                }`}
              >
                💼 Work
              </button>
              <button
                type="button"
                onClick={() => setMeetingCategory('home')}
                className={`px-4 py-3 rounded-lg font-semibold text-sm transition-all ${
                  meetingCategory === 'home'
                    ? 'bg-emerald-600 text-white border-2 border-emerald-400'
                    : 'bg-slate-800 text-slate-400 border-2 border-slate-600 hover:border-slate-500'
                }`}
              >
                🏠 Home
              </button>
              <button
                type="button"
                onClick={() => setMeetingCategory('travel')}
                className={`px-4 py-3 rounded-lg font-semibold text-sm transition-all ${
                  meetingCategory === 'travel'
                    ? 'bg-cyan-600 text-white border-2 border-cyan-400'
                    : 'bg-slate-800 text-slate-400 border-2 border-slate-600 hover:border-slate-500'
                }`}
              >
                ✈️ Travel
              </button>
              <button
                type="button"
                onClick={() => setMeetingCategory('class')}
                className={`px-4 py-3 rounded-lg font-semibold text-sm transition-all ${
                  meetingCategory === 'class'
                    ? 'bg-purple-600 text-white border-2 border-purple-400'
                    : 'bg-slate-800 text-slate-400 border-2 border-slate-600 hover:border-slate-500'
                }`}
              >
                🎓 Class
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              {meetingCategory === 'class' 
                ? 'This meeting will be linked to your active class'
                : `This meeting will appear in your ${meetingCategory.charAt(0).toUpperCase() + meetingCategory.slice(1)} tab`}
            </p>
          </div>

          {/* Virtual Room Checkbox */}
          <div className="bg-indigo-500/10 border border-indigo-400/30 rounded-lg p-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={scheduleVirtualRoom}
                onChange={(e) => setScheduleVirtualRoom(e.target.checked)}
                className="w-5 h-5 rounded border-indigo-400 text-indigo-600 focus:ring-2 focus:ring-indigo-500 cursor-pointer"
              />
              <div className="flex-1">
                <div className="font-semibold text-indigo-200 flex items-center gap-2">
                  <Video className="w-4 h-4" />
                  Schedule Virtual Room
                </div>
                <p className="text-sm text-slate-400 mt-1">
                  Automatically create a Work virtual room link for this meeting
                </p>
              </div>
            </label>
          </div>

          {/* Location/Link */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              <MapPin className="w-4 h-4 inline mr-1" />
              Location (Optional)
            </label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
              placeholder="e.g., Conference Room A"
            />
            {scheduleVirtualRoom && (
              <p className="text-xs text-indigo-300 mt-2">
                💡 Virtual room link will be automatically added
              </p>
            )}
          </div>

          {/* Attendees */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              <Users className="w-4 h-4 inline mr-1" />
              Attendees (comma-separated)
            </label>
            <input
              type="text"
              value={attendees}
              onChange={(e) => setAttendees(e.target.value)}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
              placeholder="e.g., John, Sarah, Mike"
            />
          </div>

          {/* Agenda */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Agenda / Notes
            </label>
            <textarea
              value={agenda}
              onChange={(e) => setAgenda(e.target.value)}
              rows={4}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base resize-none"
              placeholder="Meeting agenda or notes..."
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full py-4 px-6 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors touch-manipulation text-lg md:text-base mt-6"
          >
            <Plus className="w-5 h-5" />
            Add Meeting to Work
          </button>
        </form>
      </ResponsiveModal>
    </>
  );
}
